import { layerDefaultSettings, layersAtom, layerSettingsAtom, layerSettingsManyAtom, tLayerSettings } from '@/atoms';
import type { LayerData } from '@/atoms/layers.atom';
import { execute } from '@/graphql/execute';
import type { LayerSettingsQuery } from '@/graphql/graphql';
import { layerSettingsQuery } from '@/queries/layer.query';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo, useRef } from 'react';

/**
 * Per-layer settings queries, one per rendered layer. They used to refetch on
 * every mount and window focus — N requests per project open, again on every
 * alt-tab. Two changes, both observer-local so the `['layer-settings', id]`
 * cache keeps exactly the writers it had (the settings dialog and the
 * station-volumes dock, whose race guards assume no others):
 *
 * - `initialData` from the project's layer list, which selects the same
 *   `settings` fields. It only seeds an entry that does not exist yet, so the
 *   first render already has the server's settings instead of defaults; its
 *   `initialDataUpdatedAt: 0` marks it stale, so it is still revalidated once.
 * - `staleTime`, so remounts and refocuses within the window reuse the answer.
 *   Explicit invalidations (the dialog's multi-layer save) still refetch.
 */
const SETTINGS_STALE_MS = 5 * 60_000;

const settingsQueryOptions = (layerId: string, layers: LayerData[]) => ({
  queryKey: ['layer-settings', layerId],
  queryFn: () => execute(layerSettingsQuery, { id: layerId }),
  staleTime: SETTINGS_STALE_MS,
  initialData: () => {
    const listed = layers.find((l) => l.id === layerId);
    return listed ? ({ layer: { id: listed.id, settings: listed.settings } } as LayerSettingsQuery) : undefined;
  },
  initialDataUpdatedAt: 0,
});

export const useLayerSettings = (layerId: string) => {
  const [settings, setSettings] = useAtom(layerSettingsAtom(layerId));
  const layers = useAtomValue(layersAtom);
  const layerQuery = useQuery(settingsQueryOptions(layerId, layers));

  // Defaults derive from the layer's METADATA (a deviation cloud's scalar, a
  // surface difference's colormap), which the settings query does not
  // select — take the full layer from the project's layer list first.
  const defaults = layerDefaultSettings(layers.find((l) => l.id === layerId) ?? layerQuery.data?.layer);
  const remote = layerQuery.data?.layer?.settings ?? null;

  /**
   * A KEY-BY-KEY merge, not `local || remote || defaults`.
   *
   * `layerSettingsAtom` is `atomWithStorage`: a browser that opened this
   * project before a setting existed holds an object with no such KEY at all,
   * and under whole-object precedence that object wins OUTRIGHT — the key
   * reads `undefined` for ever on that machine however many times the server
   * is told otherwise. `sectionOverlays` is the case that made it visible: the
   * settings dialog seeded its form from that object and "Save for everyone"
   * then wrote the missing key back as absent, wiping a layer's overlays for
   * the whole organisation from a stale local copy.
   *
   * Layered defaults → remote → local, so a key the local object is silent
   * about falls through to the value the SERVER holds, and only then to a
   * default. Every key the local object DOES carry still wins, which is the
   * per-user override this hook exists for. The dock (`StationVolumeDock`)
   * resolves the same three sources the same way.
   */
  const merged = useMemo(
    () => ({ ...defaults, ...(remote ?? {}), ...(settings ?? {}) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(defaults), JSON.stringify(remote), settings],
  );

  return {
    settings: merged,
    setSettings,
    layer: layerQuery.data?.layer,
  };
};

export const useLayerSettingsMany = (layerIds: string[]) => {
  const getLayerSettings = useAtomValue(layerSettingsManyAtom);
  const layers = useAtomValue(layersAtom);
  const layerQueries = useQueries({
    queries: layerIds.map((layer) => settingsQueryOptions(layer, layers)),
  });
  const localSettings = getLayerSettings(layerIds);
  // `useQueries` returns results in `queries` order, so result i is layer i.
  // Read each tracked result once: looking them up per layer with `find`
  // was O(layers²) proxy reads on every recompute (~3 s while a 254-layer
  // project loaded).
  const remoteLayers = layerQueries.map((q) => q.data?.layer ?? null);

  // Stringify dependencies for stable comparison
  const localSettingsKey = JSON.stringify(localSettings);
  const remoteSettingsKey = JSON.stringify(remoteLayers.map((l) => l?.settings ?? null));

  // Per-layer values keep their identity while their content is unchanged,
  // so one layer's settings arriving doesn't re-render every other layer's
  // features (memo'd on `layerSettings[layerId]`).
  const previousRef = useRef<Record<string, { json: string; value: tLayerSettings }>>({});
  const layerSettings = useMemo(() => {
    const layersById = new Map(layers.map((l) => [l.id, l]));
    const previous = previousRef.current;
    const next: Record<string, { json: string; value: tLayerSettings }> = {};
    const out = {} as Record<string, tLayerSettings>;
    layerIds.forEach((layerId, i) => {
      const local = localSettings[layerId];
      const remoteLayer = remoteLayers[i];
      const remote = remoteLayer?.settings ?? null;
      const value = local || remote || layerDefaultSettings(layersById.get(layerId) ?? remoteLayer ?? undefined);
      const json = JSON.stringify(value);
      const prev = previous[layerId];
      next[layerId] = prev && prev.json === json ? prev : { json, value };
      out[layerId] = next[layerId].value;
    });
    previousRef.current = next;
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(layerIds), localSettingsKey, remoteSettingsKey, layers]);

  return layerSettings;
};
